import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { cartDisplayCount } from '@theme/cart-count';

const PROXY_URL = '/apps/wishlist/sync';

export class SaveForLaterStore {
  static #LOCAL_KEY = 'birch_save_for_later';
  /** Tracks which customer the locally-cached saved items belong to. */
  static #OWNER_KEY = 'birch_save_for_later_owner';
  static #syncTimer = null;

  static getItems() {
    try {
      return JSON.parse(localStorage.getItem(this.#LOCAL_KEY)) ?? [];
    } catch {
      return [];
    }
  }

  static #save(items) {
    localStorage.setItem(this.#LOCAL_KEY, JSON.stringify(items));
  }

  static get count() {
    return this.getItems().length;
  }

  static saveItem(variantId, productHandle, quantity, productTitle = '', productImage = '', sellingPlanId = '') {
    const items = this.getItems().filter((i) => i.variantId !== String(variantId));
    items.unshift({
      variantId: String(variantId),
      productHandle,
      quantity,
      productTitle,
      productImage,
      sellingPlanId: sellingPlanId ? String(sellingPlanId) : '',
      savedAt: Date.now(),
    });
    this.#save(items);
    this.#dispatch({ action: 'save', variantId: String(variantId) });
    this.syncUp();
  }

  static removeItem(variantId) {
    const items = this.getItems().filter((i) => i.variantId !== String(variantId));
    this.#save(items);
    this.#dispatch({ action: 'remove', variantId: String(variantId) });
    this.syncUp();
  }

  static #dispatch(detail) {
    document.dispatchEvent(new SaveForLaterUpdateEvent({ ...detail, count: this.count }));
  }

  /**
   * Reconcile the locally-cached saved items against the current auth state.
   * Clears local data on sign-out (or account-switch) so one shopper never
   * inherits another's saved-for-later items. Runs on every page load (guests
   * included). A guest→login transition is preserved for the merge in
   * #syncDown. Mirrors WishlistStore.reconcileOwner.
   */
  static reconcileOwner() {
    const current = Theme.customerId ? String(Theme.customerId) : null;
    let prev;
    try {
      prev = localStorage.getItem(this.#OWNER_KEY);
    } catch {
      prev = null;
    }
    if (prev && prev !== current) this.clearLocal();
    try {
      if (current) localStorage.setItem(this.#OWNER_KEY, current);
      else localStorage.removeItem(this.#OWNER_KEY);
    } catch {
      // private mode / quota — non-fatal
    }
  }

  /** Drop the locally-cached saved items and reset dependent UI. */
  static clearLocal() {
    try {
      localStorage.removeItem(this.#LOCAL_KEY);
    } catch {
      // private mode / quota — non-fatal
    }
    this.#dispatch({ action: 'clear' });
  }

  static init() {
    if (!Theme.customerId) return;
    this.#syncDown();
  }

  static async #syncDown() {
    try {
      const resp = await fetch(PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: `gid://shopify/Customer/${Theme.customerId}`,
          namespace: 'custom',
          key: 'save_for_later',
          action: 'read',
        }),
      });
      if (!resp.ok) return;
      const { value } = await resp.json();
      if (value) {
        const remote = JSON.parse(value);
        const local = this.getItems();
        const localIds = new Set(local.map((i) => i.variantId));
        for (const item of remote) {
          if (!localIds.has(item.variantId)) local.push(item);
        }
        this.#save(local);
        this.#dispatch({ action: 'sync' });
      }
      await this.#doSyncUp();
    } catch {
      // stay local silently
    }
  }

  static syncUp() {
    clearTimeout(this.#syncTimer);
    this.#syncTimer = setTimeout(() => this.#doSyncUp(), 1000);
  }

  static async #doSyncUp() {
    if (!Theme.customerId) return;
    try {
      await fetch(PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: `gid://shopify/Customer/${Theme.customerId}`,
          namespace: 'custom',
          key: 'save_for_later',
          value: JSON.stringify(this.getItems()),
          action: 'write',
        }),
      });
    } catch {
      // fail silently
    }
  }
}

export class SaveForLaterUpdateEvent extends Event {
  static eventName = 'save-for-later:update';

  constructor(detail) {
    super(SaveForLaterUpdateEvent.eventName, { bubbles: true });
    this.detail = detail;
  }
}

class SaveForLaterComponent extends Component {
  #onUpdate = () => this.#render();
  #onCartUpdate = () => {
    if (this.innerHTML.trim() === '' && SaveForLaterStore.getItems().length > 0) {
      this.#render();
    }
  };

  #dialogObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(SaveForLaterUpdateEvent.eventName, this.#onUpdate);
    document.addEventListener('cart:update', this.#onCartUpdate);
    this.#render();

    const dialog = this.closest('dialog');
    if (dialog) {
      this.#dialogObserver = new MutationObserver(() => {
        if (dialog.hasAttribute('open')) this.#render();
      });
      this.#dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    }
  }

  updatedCallback() {
    super.updatedCallback?.();
    this.#render();
  }

  render() {
    this.#render();
  }

  syncPopulatedClass() {
    this.classList.toggle('is-populated', SaveForLaterStore.getItems().length > 0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(SaveForLaterUpdateEvent.eventName, this.#onUpdate);
    document.removeEventListener('cart:update', this.#onCartUpdate);
    this.#dialogObserver?.disconnect();
  }

  #render() {
    const items = SaveForLaterStore.getItems();
    if (items.length === 0) {
      this.innerHTML = '';
      this.classList.remove('is-populated');
      return;
    }

    this.classList.add('is-populated');
    this.innerHTML = `
      <div class="save-for-later">
        <h3 class="save-for-later__heading">${Theme.translations.saved_for_later ?? 'Saved for Later'} (${items.length})</h3>
        <ul class="save-for-later__list list-unstyled">
          ${items.map((item) => this.#renderItem(item)).join('')}
        </ul>
      </div>
    `;

    this.querySelectorAll('[data-move-to-cart]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.stopPropagation();
        event.preventDefault();
        this.#moveToCart(btn.dataset.variantId, btn.dataset.quantity, btn.dataset.sellingPlanId);
      });
    });
    this.querySelectorAll('[data-remove-saved]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.stopPropagation();
        event.preventDefault();
        SaveForLaterStore.removeItem(btn.dataset.variantId);
      });
    });
  }

  #renderItem(item) {
    const imageHtml = item.productImage
      ? `<a href="/products/${item.productHandle}" class="save-for-later__item-media-link" tabindex="-1" aria-hidden="true">
          <img src="${item.productImage}" alt="${item.productTitle || item.productHandle}" class="save-for-later__item-image" width="60" height="60" loading="lazy">
         </a>`
      : '';
    const titleText = item.productTitle || item.productHandle;
    return `
      <li class="save-for-later__item" data-variant-id="${item.variantId}">
        ${imageHtml}
        <div class="save-for-later__item-info">
          <a href="/products/${item.productHandle}" class="save-for-later__item-link">${titleText}</a>
        </div>
        <div class="save-for-later__item-actions">
          <button class="button save-for-later__move-btn" type="button"
            data-move-to-cart data-variant-id="${item.variantId}" data-quantity="${item.quantity}"
            data-selling-plan-id="${item.sellingPlanId || ''}">
            ${Theme.translations.move_to_cart ?? 'Move to cart'}
          </button>
          <button class="button save-for-later__remove-btn button-unstyled" type="button"
            data-remove-saved data-variant-id="${item.variantId}"
            aria-label="Remove ${titleText}">
            <span class="svg-wrapper" aria-hidden="true">✕</span>
          </button>
        </div>
      </li>
    `;
  }

  async #moveToCart(variantId, quantity, sellingPlanId) {
    try {
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);

      const resp = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: parseInt(quantity, 10) || 1,
              ...(sellingPlanId ? { selling_plan: sellingPlanId } : {}),
            },
          ],
          sections: sectionIds.join(','),
        }),
      });
      if (!resp.ok) return;
      const data = await resp.json();
      SaveForLaterStore.removeItem(variantId);

      // /cart/add.js returns only the lines just added, so the header bubble needs
      // the whole cart to count from (as the wishlist's "Add all" does). If that
      // read fails, the event adds the moved quantity to the bubble instead.
      let cart = null;
      try {
        const cartResp = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
        if (cartResp.ok) cart = await cartResp.json();
      } catch {
        // fall back to the delta below
      }
      document.dispatchEvent(
        cart
          ? new CartAddEvent(cart, '', {
              source: 'save-for-later',
              variantId,
              // Not `item_count` — the bubble counts a bundle once. See cart-count.js.
              itemCount: cartDisplayCount(cart),
              sections: data.sections,
            })
          : new CartAddEvent(data, '', {
              source: 'product-form-component',
              variantId,
              itemCount: parseInt(quantity, 10) || 1,
              sections: data.sections,
            })
      );
    } catch {
      // fail silently
    }
  }
}

if (!customElements.get('save-for-later-component')) {
  customElements.define('save-for-later-component', SaveForLaterComponent);
}
